import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Slider } from "../../components/ui/slider";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  Bot,
  Settings,
  AlertTriangle,
  CheckCircle,
  Activity,
  Database,
} from "lucide-react";
function AiSettings() {
  const [confidenceThreshold, setConfidenceThreshold] = useState([85]);
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(false);
  const [riskAssessmentEnabled, setRiskAssessmentEnabled] = useState(true);

  const aiMetrics = {
    accuracy: 94.2,
    totalAnalyses: 2847,
    avgProcessingTime: "1.2s",
    falsePositives: 12,
    falseNegatives: 8,
  };
  return (
    <>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            AI Settings & Configuration
          </h1>
          <p className="text-slate-600">
            Configure AI analysis parameters and monitor performance
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="!p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    AI Accuracy
                  </p>
                  <p className="text-3xl font-bold text-slate-900">
                    {aiMetrics.accuracy}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-success bg-opacity-10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-success" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    Total Analyses
                  </p>
                  <p className="text-3xl font-bold text-slate-900">
                    {aiMetrics.totalAnalyses}
                  </p>
                </div>
                <div className="w-12 h-12 bg-info bg-opacity-10 rounded-lg flex items-center justify-center">
                  <Database className="text-info" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    Avg Processing
                  </p>
                  <p className="text-3xl font-bold text-slate-900">
                    {aiMetrics.avgProcessingTime}
                  </p>
                </div>
                <div className="w-12 h-12 bg-xaxathrive-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                  <Activity className="text-xaxathrive-primary" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    Error Rate
                  </p>
                  <p className="text-3xl font-bold text-slate-900">0.7%</p>
                </div>
                <div className="w-12 h-12 bg-warning bg-opacity-10 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="text-warning" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot size={20} />
                Analysis Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-sm font-medium">
                  Confidence Threshold
                </Label>
                <div className="mt-2">
                  <Slider
                    value={confidenceThreshold}
                    onValueChange={setConfidenceThreshold}
                    max={100}
                    min={50}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>50%</span>
                    <span className="font-medium">
                      Current: {confidenceThreshold[0]}%
                    </span>
                    <span>100%</span>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Claims with AI confidence below this threshold will require
                  manual review
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">
                      Auto-Approval for High Confidence
                    </Label>
                    <p className="text-xs text-slate-600">
                      Automatically approve claims above 95% confidence
                    </p>
                  </div>
                  <Switch
                    checked={autoApprovalEnabled}
                    onCheckedChange={setAutoApprovalEnabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">
                      Advanced Risk Assessment
                    </Label>
                    <p className="text-xs text-slate-600">
                      Enable pattern detection and fraud analysis
                    </p>
                  </div>
                  <Switch
                    checked={riskAssessmentEnabled}
                    onCheckedChange={setRiskAssessmentEnabled}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">
                  Processing Priority
                </Label>
                <Select defaultValue="balanced">
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="speed">Speed Optimized</SelectItem>
                    <SelectItem value="balanced">Balanced</SelectItem>
                    <SelectItem value="accuracy">Accuracy Optimized</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity size={20} />
                Model Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-medium">
                    Overall Accuracy
                  </Label>
                  <span className="text-sm font-bold text-success">
                    {aiMetrics.accuracy}%
                  </span>
                </div>
                <Progress value={aiMetrics.accuracy} className="!h-2" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-lg font-bold text-error">
                    {aiMetrics.falsePositives}
                  </p>
                  <p className="text-xs text-slate-600">False Positives</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-lg font-bold text-warning">
                    {aiMetrics.falseNegatives}
                  </p>
                  <p className="text-xs text-slate-600">False Negatives</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">
                  Recent Performance Trend
                </Label>
                <div className="mt-2 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-600">Last 7 days</span>
                    <Badge className="bg-success text-white">+2.1%</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-600">Last 30 days</span>
                    <Badge className="bg-success text-white">+5.3%</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-600">This quarter</span>
                    <Badge className="bg-success text-white">+12.7%</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database size={20} />
                Training Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-lg font-bold text-slate-900">15,847</p>
                  <p className="text-xs text-slate-600">Training Claims</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-lg font-bold text-slate-900">2,341</p>
                  <p className="text-xs text-slate-600">Validation Set</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Last Model Update</Label>
                <p className="text-sm text-slate-600 mt-1">
                  June 25, 2024 - 14:32 UTC
                </p>
              </div>

              <div className="space-y-2">
                <Button
                  className="w-full bg-xaxathrive-primary hover:bg-blue-700"
                  size="sm"
                >
                  Retrain Model
                </Button>
                <Button variant="outline" className="w-full" size="sm">
                  Download Training Logs
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle size={20} />
                Alert Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium">
                  Performance Alerts
                </Label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Accuracy drops below</span>
                    <Input
                      type="number"
                      defaultValue="90"
                      className="!w-16 h-8 text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Processing time exceeds</span>
                    <Input
                      type="number"
                      defaultValue="5"
                      className="!w-16 h-8 text-xs"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">
                  Notification Recipients
                </Label>
                <Textarea
                  rows={3}
                  placeholder="admin@xaxathrive.com, ai-team@xaxathrive.com"
                  className="mt-2 text-sm"
                />
              </div>

              <Button variant="outline" className="w-full" size="sm">
                Test Alert System
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="!p-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-slate-900">
                  Configuration Changes
                </h3>
                <p className="text-sm text-slate-600">
                  Save your AI settings to apply them to new claims
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline">Reset to Defaults</Button>
                <Button className="bg-success hover:bg-green-600">
                  Save Configuration
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
export default AiSettings;
